import React, {FormEvent, useEffect, useState} from "react";
import {Alert, Container, Paper, TextField, Typography} from "@mui/material";
import {Navigate, useNavigate, useSearchParams} from "react-router-dom";
import Loading from "./components/loading";
import AlreadyRenewed from "./components/already-renewed";
import CompanyNotFound from "./components/company-not-found";
import CompanyRenew from "./components/company-renew";
import ResponsiveAppBar from "./components/responsive-app-bar";
import Footer from "./components/home/Footer";
import {DarkButton} from "./components/buttons";
import {determineLocation} from "./assets/determineLocation";

export default function Renew() {
    const [params, setParams] = useSearchParams();
    const [input, setInput] = useState("");
    const [status, setStatus] = useState(0);
    const [error, setError] = useState("");
    const documentNumber = params.get("d");
    const navigate = useNavigate();

    useEffect(() => {
        if (!documentNumber) return;
        const controller = new AbortController();
        setStatus(0);
        setError("");
        async function check() {
            try {
                const url = `${determineLocation()}/company/${encodeURIComponent(documentNumber!)}`;
                let body = await (await fetch(`${url}/status`, {signal: controller.signal})).json();
                if (body.code === 500) body = await (await fetch(`${url}/status2`, {signal: controller.signal})).json();
                if (body.code === 202) navigate(`/receipt?receipt=${encodeURIComponent(body.message.id)}`, {replace: true});
                setStatus(body.code);
            } catch (reason) {
                if (!controller.signal.aborted) setError("We could not verify your company. Please try again shortly.");
            }
        }
        void check();
        return () => controller.abort();
    }, [documentNumber, navigate]);

    if (documentNumber && !error) {
        if (status === 0 || status === 202) return <Loading/>;
        if (status === 200) return <AlreadyRenewed/>;
        if (status === 404) return <CompanyNotFound/>;
        if (status === 405) return <Navigate to={`/reinstate?d=${encodeURIComponent(documentNumber)}`} replace/>;
        if (status === 406) return <CompanyRenew documentNumber={documentNumber}/>;
    }

    return <>
        <ResponsiveAppBar showLogin={true}/>
        <Container maxWidth="sm" sx={{py: 6}}>
            <Typography variant="h3">Renew a Florida company</Typography>
            {!documentNumber ? <Paper component="form" sx={{p: 3, mt: 3}} onSubmit={(event: FormEvent) => {
                event.preventDefault();
                const value = input.trim();
                if (value) setParams({d: value.length < 15 ? value.toUpperCase() : value});
            }}>
                <TextField label="Florida document number" fullWidth required value={input} onChange={event => setInput(event.target.value)}/>
                <DarkButton type="submit" variant="contained" sx={{mt: 2}}>Check renewal eligibility</DarkButton>
            </Paper> : <Alert severity={status === 407 ? "info" : "warning"} sx={{mt: 3}}>
                {status === 407 ? "This company was formed this year and does not yet need an annual report."
                    : error || "Renewal eligibility could not be confirmed. Please contact support or try again shortly."}
            </Alert>}
        </Container>
        <Footer/>
    </>;
}
